import { useState, useEffect } from "react";
import { Search, Loader, UserPlus, X, Plus } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

const CreateGroupModal = ({ onClose, onCreated }) => {
  const [name, setName]           = useState("");
  const [description, setDesc]    = useState("");
  const [customers, setCustomers] = useState([]);
  const [custLoading, setCustLoad] = useState(true);
  const [search, setSearch]       = useState("");
  const [selected, setSelected]   = useState(new Set());
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    api.get("/customers/by-user")
      .then((r) => setCustomers(r.data.customers || r.data.data || []))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setCustLoad(false));
  }, []);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.phoneNumber?.includes(q)
    );
  });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map((c) => c.id)));
  const clearAll  = () => setSelected(new Set());

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Group name is required"); return; }
    setSaving(true);
    try {
      // 1. Create group
      const gr = await api.post("/groups/create", { name: name.trim(), description: description.trim() || undefined });
      const newGroup = gr.data.group;

      // 2. Add selected customers
      if (selected.size > 0) {
        await api.post(`/group-members/add-multiple/${newGroup.id}`, {
          customerIds: [...selected],
        });
      }

      toast.success(`Group "${newGroup.name}" created with ${selected.size} contacts`);
      onCreated({ ...newGroup, activeCustomerCount: selected.size, userCustomerCount: selected.size });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">Create New Group</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Name <span className="text-red-400">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. VIP Customers"
              className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              value={description}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="What is this group for?"
              className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Contact picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Add Contacts
                {selected.size > 0 && (
                  <span className="ml-1.5 text-xs bg-blue-100 text-blue-700 font-semibold px-1.5 py-0.5 rounded-full">
                    {selected.size} selected
                  </span>
                )}
              </label>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-xs text-blue-600 hover:underline">All</button>
                <span className="text-gray-300">·</span>
                <button onClick={clearAll} className="text-xs text-gray-400 hover:underline">Clear</button>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-2">
              <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or phone…"
                className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
              />
            </div>

            <div className="border border-gray-100 rounded-xl overflow-hidden" style={{ maxHeight: 240 }}>
              {custLoading ? (
                <div className="flex justify-center py-8">
                  <Loader className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-400">
                  {customers.length === 0 ? "No contacts yet." : "No contacts match your search."}
                </div>
              ) : (
                <div className="overflow-y-auto divide-y divide-gray-50" style={{ maxHeight: 240 }}>
                  {filtered.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                        className="w-4 h-4 accent-blue-600 flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{c.name || "—"}</p>
                        <p className="text-xs text-gray-400">{c.phoneNumber}</p>
                      </div>
                      {c.status === false && (
                        <span className="text-[10px] bg-red-50 text-red-400 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          inactive
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !name.trim()}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-xl transition-colors"
          >
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Group{selected.size > 0 ? ` (${selected.size} contacts)` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;
